import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getRoleByEmail, UserRole, canUseDebugView } from "../config/roles";

/**
 * ⚠️ DEMO MODE: OPEN LOGIN (NO PASSWORD)
 *
 * User request: "一天展示用，刪除登入保護。三個帳號輸入就直接進入。"
 *
 * - This intentionally disables password verification.
 * - Only whitelisted emails are accepted.
 * - Remove/disable this before any real release.
 */
const DEMO_OPEN_LOGIN = true;

// Only allow these 3 demo accounts
const DEMO_USERS: Record<string, { displayName: string } & Partial<Pick<AppUser, "role">>> = {
  "kc@lg.app": { displayName: "法務主管" },
  "cfo@lg.app": { displayName: "財務長" },
  "gm@lg.app": { displayName: "總經理" },
};

const DEMO_LAST_EMAIL_KEY = "legalops.demo.lastEmail";

export type { UserRole };

export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  displayName: string;
};

type AuthContextValue = {
  user: AppUser | null;
  isAuthenticated: boolean;
  isSealUser: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  debugViewDepartmentId: string | null;
  setDebugViewDepartmentId: (id: string | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function normalizeEmail(email: string): string {
  return String(email || "").toLowerCase().trim();
}

function buildDemoUser(email: string): AppUser {
  const e = normalizeEmail(email);
  const meta = DEMO_USERS[e];
  const displayName = meta?.displayName || e.split("@")[0];
  const role = meta?.role || getRoleByEmail(e);
  return {
    id: e,
    name: displayName,
    email: e,
    role,
    displayName,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugViewDepartmentId, _setDebugViewDepartmentId] = useState<string | null>(null);

  const isAuthenticated = !!user;
  const isSealUser = user?.role === "sealUser";

  // Restore last demo login (so refresh doesn't kick you out during demo)
  useEffect(() => {
    if (!DEMO_OPEN_LOGIN) {
      setLoading(false);
      return;
    }

    try {
      const last = window.localStorage.getItem(DEMO_LAST_EMAIL_KEY);
      if (last && DEMO_USERS[normalizeEmail(last)]) {
        setUser(buildDemoUser(last));
      }
    } catch {
      // ignore
    }

    setLoading(false);
  }, []);

  async function login(email: string, _password: string) {
    const e = normalizeEmail(email);

    if (!DEMO_OPEN_LOGIN) {
      // If you ever re-enable real auth, implement it here.
      throw new Error("登入功能目前僅供展示用（DEMO OPEN LOGIN）。");
    }

    if (!DEMO_USERS[e]) {
      throw new Error("僅開放展示帳號：kc@lg.app / cfo@lg.app / gm@lg.app");
    }

    const u = buildDemoUser(e);
    setUser(u);
    _setDebugViewDepartmentId(null);

    try {
      window.localStorage.setItem(DEMO_LAST_EMAIL_KEY, e);
    } catch {
      // ignore
    }
  }

  async function logout() {
    setUser(null);
    _setDebugViewDepartmentId(null);
    try {
      window.localStorage.removeItem(DEMO_LAST_EMAIL_KEY);
    } catch {
      // ignore
    }
  }

  const setDebugViewDepartmentId = (id: string | null) => {
    if (canUseDebugView(user?.email)) {
      _setDebugViewDepartmentId(id);
    } else {
      _setDebugViewDepartmentId(null);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0f0f10",
          color: "#fff",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isSealUser,
        login,
        logout,
        debugViewDepartmentId,
        setDebugViewDepartmentId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
